/**
 * Hooks for admin feature flag access
 */

import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useOrgContext } from "../contexts/OrgContext";

// =============================================================================
// Types
// =============================================================================

export interface FlagEvaluation {
  enabled: boolean;
  source: "tier" | "override" | "role_override" | "default" | "percentage" | "superadmin";
  variant?: string;
}

export type FlagEvaluationResult = Record<string, FlagEvaluation>;

// =============================================================================
// Hooks
// =============================================================================

const ADMIN_STALE_TIME = 5 * 60 * 1000; // 5 minutes
const VISITOR_STALE_TIME = 60 * 1000; // 1 minute

/**
 * Fetch all admin feature flags for a specific org
 * @param orgId - Organization ID to evaluate flags for
 */
export function useFeatureFlags(orgId?: string) {
  return useQuery({
    queryKey: ["feature-flags", orgId],
    queryFn: async () => {
      const res = await api["feature-flags"].evaluate.$get({
        query: orgId ? { orgId } : {},
      });
      if (!res.ok) {
        const error = (await res.json()) as { error?: string };
        throw new Error(error.error ?? "Failed to fetch feature flags");
      }
      const data = await res.json();
      return data.flags as FlagEvaluationResult;
    },
    staleTime: ADMIN_STALE_TIME,
    enabled: !!orgId,
  });
}

/**
 * Get a single feature flag by key
 * Returns { enabled, loading, variant }
 * Must be used within OrgProvider context
 */
export function useFeatureFlag(key: string) {
  const { orgId } = useOrgContext();
  const { data, isLoading, error } = useFeatureFlags(orgId);

  const flag = data?.[key];

  return {
    enabled: flag?.enabled ?? false,
    loading: isLoading,
    variant: flag?.variant,
    error,
  };
}

// =============================================================================
// Visitor Hooks
// =============================================================================

/**
 * Fetch all visitor feature flags for an organization
 */
export function useVisitorFlags(orgSlug: string) {
  const { data, isLoading } = useQuery({
    queryKey: ["visitor-flags", orgSlug],
    queryFn: async () => {
      const res = await api["feature-flags"].evaluate.visitor[":orgSlug"].$get({
        param: { orgSlug },
      });
      if (!res.ok) {
        const error = (await res.json()) as { error?: string };
        throw new Error(error.error ?? "Failed to fetch visitor flags");
      }
      const data = await res.json();
      return data.flags as FlagEvaluationResult;
    },
    staleTime: VISITOR_STALE_TIME,
    enabled: !!orgSlug,
  });

  return { flags: data, isLoading };
}

/**
 * Get a single visitor feature flag by key
 * Returns { enabled, loading, variant }
 */
export function useVisitorFlag(orgSlug: string, key: string) {
  const { flags, isLoading } = useVisitorFlags(orgSlug);

  const flag = flags?.[key];

  return {
    enabled: flag?.enabled ?? false,
    loading: isLoading,
    variant: flag?.variant,
  };
}
